import React, { useState, useEffect, useCallback } from "react";
import { 
  Row, 
  Col, 
  Button, 
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label
} from "reactstrap";
import Widget from "../../components/Widget/Widget";
import MUIDataTable from "mui-datatables";

import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment";
import classNames from "classnames";

import s from "./DeviceManagement.module.scss";

const DeviceManagement = () => {
  const [devices, setDevices] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    online: 0,
    offline: 0,
    maintenance: 0,
    error: 0
  });
  const searchTerm = "";
  const filterStatus = "all";

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [addDeviceModal, setAddDeviceModal] = useState(false);
  const [editDeviceModal, setEditDeviceModal] = useState(false);
  const [newDevice, setNewDevice] = useState({
    thing_name: '',
    asset_type: '',
    type: '',
    location_postal: '',
    remote_url: '',
    lat: '',
    lon: '',
    facility_name: '',
    status: 'offline'
  });
  const [editDevice, setEditDevice] = useState({
    device_id: '',
    thing_name: '',
    asset_type: '',
    type: '',
    location_postal: '',
    remote_url: '',
    lat: '',
    lon: '',
    facility_name: '',
    status: 'offline'
  });
  const [saving, setSaving] = useState(false);

  const [maintenanceTab, setMaintenanceTab] = useState("facility");
  const [facilityHistory, setFacilityHistory] = useState({
    data: [],
    total: 0,
    page: 0,
    rowsPerPage: 10,
    search: "",
    sort: { name: "endTime", direction: "desc" },
    loading: false,
  });
  const [buildingHistory, setBuildingHistory] = useState({
    data: [],
    total: 0,
    page: 0,
    rowsPerPage: 10,
    search: "",
    sort: { name: "endTime", direction: "desc" },
    loading: false,
  });

  // Fetch device statistics from backend
  const fetchStatistics = async () => {
    try {
      const response = await axios.get("/devices/statistics");
      setStatistics(response.data);
    } catch (error) {
      console.error("Error fetching device statistics:", error);
      toast.error("Failed to fetch device statistics");
    }
  };

  // Fetch devices from backend
  const fetchDevices = useCallback(async (realTimeStatus = false) => {
    try {
      const params = {
        search: searchTerm || undefined,
        status: filterStatus !== "all" ? filterStatus : undefined,
        real_time_status: realTimeStatus,
        limit: 100 // Get more devices for demo
      };

      // Remove undefined params
      Object.keys(params).forEach(key => 
        params[key] === undefined && delete params[key]
      );

      const response = await axios.get("/devices", { params });
      
      // Transform backend data to match frontend expectations
      const transformedDevices = response.data.devices.map(device => ({
        id: device.device_id,
        name: device.thing_name || `Device ${device.device_id}`,
        type: device.type || device.asset_type || "Unknown",
        location: device.location_postal || "Unknown Location",
        facilityName: device.facility_name || "N/A",
        status: device.real_time_status || device.status,
        lastSeen: device.date_updated ? new Date(device.date_updated).toLocaleString() : "Never",
        ipAddress: device.remote_url || "N/A",
        firmware: "v1.0.0",
        health: device.status === "online" ? "good" : 
                device.status === "offline" ? "warning" : 
                device.status === "error" ? "critical" : "unknown",
        thing_name: device.thing_name,
        device_id: device.device_id,
        isRealTime: device.status_source === "iot_batch",
        lat: device.lat || '',
        lon: device.lon || '',
      }));

      setDevices(transformedDevices);
      return response.data;
    } catch (error) {
      console.error("Error fetching devices:", error);
      toast.error("Failed to fetch devices");
      return null;
    }
  }, [searchTerm, filterStatus]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStatistics(),
        fetchDevices(true) // Use real-time status on initial load
      ]);
      setLoading(false);
    };

    loadData();
  }, [fetchDevices]);

  // Reload data when filters change
  useEffect(() => {
    if (!loading) {
      fetchDevices(false); // Use cached status for filter changes
    }
  }, [fetchDevices, loading]);

  // Refresh all data with real-time status
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchStatistics(),
      fetchDevices(true) // Force real-time status check
    ]);
    setRefreshing(false);
    toast.success("Device status refreshed");
  };

  const updateHistoryState = (tab, updater) => {
    if (tab === "facility") {
      setFacilityHistory((prev) => updater(prev));
    } else {
      setBuildingHistory((prev) => updater(prev));
    }
  };

  const getHistoryState = (tab) =>
    tab === "facility" ? facilityHistory : buildingHistory;

  const fetchMaintenanceHistory = async (
    tab,
    overrides = {}
  ) => {
    const currentState = getHistoryState(tab);
    const params = {
      page: overrides.page ?? currentState.page,
      limit: overrides.limit ?? currentState.rowsPerPage,
      search: overrides.search ?? currentState.search,
      sort: overrides.sort ?? currentState.sort,
    };

    updateHistoryState(tab, (prev) => ({ ...prev, loading: true }));

    try {
      const response = await axios.get("/devices/maintenance-history", {
        params: {
          page: params.page + 1,
          limit: params.limit,
          search: params.search || undefined,
          sort_by: params.sort?.name,
          sort_direction: params.sort?.direction,
          source: tab,
        },
      });
      const { items = [], total = 0 } = response.data || {};

      updateHistoryState(tab, (prev) => ({
        ...prev,
        data: items,
        total,
        page: params.page,
        rowsPerPage: params.limit,
        search: params.search,
        sort: params.sort,
        loading: false,
      }));
    } catch (error) {
      console.error("Error fetching maintenance history:", error);
      toast.error("Failed to fetch maintenance history");
      updateHistoryState(tab, (prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchMaintenanceHistory("facility", { page: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const state = getHistoryState(maintenanceTab);
    if (!state.data.length && !state.loading) {
      fetchMaintenanceHistory(maintenanceTab, { page: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maintenanceTab]);

  const handleMaintenanceTabChange = (tab) => {
    if (tab === maintenanceTab) return;
    setMaintenanceTab(tab);
  };

  // Remote access handler - opens device URL in new tab
  const handleRemoteAccess = (device) => {
    if (!device.ipAddress || device.ipAddress === "N/A") {
      toast.error("No remote URL available for this device");
      return;
    }
    
    let url = device.ipAddress;
    // Add protocol if not present
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    // Open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success(`Opening remote access for ${device.name}`);
  };

  // Delete device
  const handleDeleteDevice = async (deviceId) => {
    if (window.confirm("Are you sure you want to delete this device?")) {
      try {
        await axios.delete(`/devices/${deviceId}`);
        toast.success("Device deleted successfully");
        await fetchDevices(); // Refresh the list
        await fetchStatistics(); // Refresh stats
      } catch (error) {
        console.error("Error deleting device:", error);
        toast.error("Failed to delete device");
      }
    }
  };

  // Add device functionality
  const toggleAddDeviceModal = () => {
    setAddDeviceModal(!addDeviceModal);
    if (addDeviceModal) {
      // Reset form when closing
      setNewDevice({
        thing_name: '',
        asset_type: '',
        type: '',
        location_postal: '',
        remote_url: '',
        lat: '',
        lon: '',
        facility_name: '',
        status: 'offline'
      });
    }
  };

  // Edit device functionality
  const toggleEditDeviceModal = () => {
    setEditDeviceModal(!editDeviceModal);
    if (editDeviceModal) {
      // Reset form when closing
      setEditDevice({
        device_id: '',
        thing_name: '',
        asset_type: '',
        type: '',
        location_postal: '',
        remote_url: '',
        lat: '',
        lon: '',
        facility_name: '',
        status: 'offline'
      });
    }
  };

  const handleEditDevice = (device) => {
    setEditDevice({
      device_id: device.device_id,
      thing_name: device.thing_name || '',
      asset_type: device.type || '',
      type: device.type || '',
      location_postal: device.location || '',
      remote_url: device.ipAddress !== 'N/A' ? device.ipAddress : '',
      lat: device.lat || '',
      lon: device.lon || '',
      facility_name: device.facilityName !== 'N/A' ? device.facilityName : '',
      status: device.status
    });
    setEditDeviceModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDevice(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditDevice(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddDevice = async (e) => {
    e.preventDefault();
    
    if (!newDevice.thing_name.trim()) {
      toast.error("Thing name is required");
      return;
    }

    setSaving(true);
    try {
      await axios.post('/devices', newDevice);
      toast.success("Device added successfully");
      toggleAddDeviceModal();
      await fetchDevices(); // Refresh the list
      await fetchStatistics(); // Refresh stats
    } catch (error) {
      console.error("Error adding device:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to add device");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDevice = async (e) => {
    e.preventDefault();
    
    if (!editDevice.thing_name.trim()) {
      toast.error("Thing name is required");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`/devices/${editDevice.device_id}`, editDevice);
      toast.success("Device updated successfully");
      toggleEditDeviceModal();
      await fetchDevices(); // Refresh the list
      await fetchStatistics(); // Refresh stats
    } catch (error) {
      console.error("Error updating device:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to update device");
      }
    } finally {
      setSaving(false);
    }
  };

  const maintenanceColumns =
    maintenanceTab === "facility"
      ? [
          {
            name: "source",
            label: "Source",
            options: {
              filter: true,
              sort: true,
            },
          },
          {
            name: "facilityName",
            label: "Facility",
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value) => value || "-",
            },
          },
          {
            name: "testStatus",
            label: "Test Status",
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value) => value || "-",
            },
          },
          {
            name: "testMessage",
            label: "Test Message",
            options: {
              filter: false,
              sort: true,
              customBodyRender: (value) => value || "-",
            },
          },
          {
            name: "testTime",
            label: "Test Time",
            options: {
              filter: false,
              sort: true,
              customBodyRender: (value) =>
                value && moment(value).isValid()
                  ? moment(value).format("DD MMM YYYY HH:mm")
                  : "-",
            },
          },
          {
            name: "assignedTo",
            label: "Assigned To",
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value) => value || "-",
            },
          },
          {
            name: "notes",
            label: "Notes",
            options: {
              filter: false,
              sort: true,
              customBodyRender: (value) => value || "-",
            },
          },
        ]
      : [
          {
            name: "source",
            label: "Source",
            options: {
              filter: true,
              sort: true,
            },
          },
          {
            name: "facilityName",
            label: "Facility",
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value) => value || "-",
            },
          },
          {
            name: "buildingName",
            label: "Building",
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value) => value || "-",
            },
          },
          {
            name: "category",
            label: "Event Type",
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value) =>
                value
                  ? value
                      .toString()
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())
                  : "-",
            },
          },
          {
            name: "condition",
            label: "Condition",
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value) => value || "-",
            },
          },
          {
            name: "assignedTo",
            label: "Assigned To",
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value) => value || "-",
            },
          },
          {
            name: "startTime",
            label: "Start Time",
            options: {
              filter: false,
              sort: true,
              customBodyRender: (value) =>
                value && moment(value).isValid()
                  ? moment(value).format("DD MMM YYYY HH:mm")
                  : "-",
            },
          },
          {
            name: "endTime",
            label: "End Time",
            options: {
              filter: false,
              sort: true,
              customBodyRender: (value) =>
                value && moment(value).isValid()
                  ? moment(value).format("DD MMM YYYY HH:mm")
                  : "-",
            },
          },
          {
            name: "status",
            label: "Status",
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value) =>
                value
                  ? value
                      .toString()
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())
                  : "-",
            },
          },
          {
            name: "notes",
            label: "Notes",
            options: {
              filter: false,
              sort: true,
              customBodyRender: (value) => value || "-",
            },
          },
        ];

  const activeHistoryState =
    maintenanceTab === "facility" ? facilityHistory : buildingHistory;

  const maintenanceOptions = {
    filter: true,
    search: true,
    selectableRows: "none",
    download: false,
    print: false,
    viewColumns: false,
    responsive: "standard",
    serverSide: true,
    sort: true,
    sortOrder: {
      name: activeHistoryState.sort.name,
      direction: activeHistoryState.sort.direction,
    },
    count: activeHistoryState.total,
    page: activeHistoryState.page,
    rowsPerPage: activeHistoryState.rowsPerPage,
    rowsPerPageOptions: [5, 10, 20, 50],
    searchText: activeHistoryState.search,
    textLabels: {
      body: {
        noMatch: activeHistoryState.loading
          ? "Loading..."
          : "No maintenance history found",
      },
    },
    onTableChange: (action, tableState) => {
      const nextSort =
        tableState.sortOrder && tableState.sortOrder.name
          ? {
              name: tableState.sortOrder.name,
              direction: tableState.sortOrder.direction,
            }
          : activeHistoryState.sort;
      const nextSearch = tableState.searchText || "";
      const nextLimit = tableState.rowsPerPage;
      switch (action) {
        case "changePage":
          fetchMaintenanceHistory(maintenanceTab, {
            page: tableState.page,
            limit: nextLimit,
            search: nextSearch,
            sort: nextSort,
          });
          break;
        case "changeRowsPerPage":
          fetchMaintenanceHistory(maintenanceTab, {
            page: 0,
            limit: nextLimit,
            search: nextSearch,
            sort: nextSort,
          });
          break;
        case "search":
          if (
            nextSearch !== activeHistoryState.search &&
            !activeHistoryState.loading
          ) {
            fetchMaintenanceHistory(maintenanceTab, {
              page: 0,
              limit: nextLimit,
              search: nextSearch,
              sort: nextSort,
            });
          }
          break;
        case "resetFilters":
          fetchMaintenanceHistory(maintenanceTab, {
            page: 0,
            limit: nextLimit,
            search: "",
            sort: nextSort,
          });
          break;
        case "sort":
          if (tableState.sortOrder?.name) {
            fetchMaintenanceHistory(maintenanceTab, {
              page: 0,
              limit: nextLimit,
              search: nextSearch,
              sort: nextSort,
            });
          }
          break;
        default:
          break;
      }
    },
  };

  const getStatusDot = (status, isRealTime = false) => {
    const statusConfig = {
      online: { color: "#28a745", text: "Online" },
      offline: { color: "#dc3545", text: "Offline" },
      maintenance: { color: "#ffc107", text: "Maintenance" },
      error: { color: "#dc3545", text: "Error" },
    };

    const config = statusConfig[status] || { color: "#6c757d", text: "Unknown" };
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div 
          style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: config.color,
            display: 'inline-block'
          }}
        />
        <span style={{ fontSize: '14px' }}>
          {isRealTime && <span title="Real-time IoT status"></span>}
          {config.text}
        </span>
      </div>
    );
  };



  const columns = [
    {
      name: "id",
      label: "Device ID",
      options: {
        filter: false,
        sort: true,
      }
    },
    {
      name: "name",
      label: "Device Name",
      options: {
        filter: false,
        sort: true,
      }
    },
    {
      name: "type",
      label: "Type",
      options: {
        filter: true,
        sort: true,
      }
    },
    {
      name: "location",
      label: "Location",
      options: {
        filter: false,
        sort: true,
      }
    },
    {
      name: "facilityName",
      label: "Facility Name",
      options: {
        filter: true,
        sort: true,
      }
    },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const device = devices[tableMeta.rowIndex];
          return getStatusDot(value, device?.isRealTime);
        }
      }
    },
    {
      name: "lastSeen",
      label: "Last Seen",
      options: {
        filter: false,
        sort: true,
      }
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const device = devices[tableMeta.rowIndex];
          // const isOnline = device.status === "online";
          const hasRemoteUrl = device.ipAddress && device.ipAddress !== "N/A";
          
          return (
            <div className={s.actionButtons}>
              <Button
                color="link"
                size="sm"
                disabled={!hasRemoteUrl}
                onClick={() => handleRemoteAccess(device)}
                className="mr-2 p-1"
                style={{ 
                  textDecoration: hasRemoteUrl ? 'underline' : 'none',
                  color: hasRemoteUrl ? '#007bff' : '#6c757d',
                  fontSize: '13px'
                }}
                title={hasRemoteUrl ? 
                  `Open ${device.ipAddress}` : 
                  "No remote URL available"
                }
              >
                Remote Access
              </Button>
              <UncontrolledDropdown>
                <DropdownToggle 
                  caret 
                  color="link" 
                  size="sm"
                  style={{ 
                    textDecoration: 'underline',
                    color: '#007bff',
                    fontSize: '13px',
                    padding: '2px 8px'
                  }}
                >
                  More
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={() => handleEditDevice(device)}>
                    <i className="eva eva-edit-outline mr-1"></i>
                    Edit Device
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem onClick={() => handleDeleteDevice(device.device_id)} className="text-danger">
                    <i className="eva eva-trash-2-outline mr-1"></i>
                    Delete Device
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          );
        }
      }
    }
  ];

  const options = {
    filterType: "checkbox",
    responsive: "standard",
    selectableRows: "none",
    download: true,
    print: true,
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 20, 50],
    customToolbar: () => {
      return (
        <div className="d-flex">
          <Button
            color="success"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="mr-2"
          >
            {refreshing ? (
              <Spinner size="sm" className="mr-1" />
            ) : (
              <i className="eva eva-refresh-outline mr-1"></i>
            )}
            Refresh Status
          </Button>
        <Button
          color="primary"
          size="sm"
            onClick={toggleAddDeviceModal}
        >
          <i className="eva eva-plus-outline mr-1"></i>
          Add Device
        </Button>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="mt-3">Loading device data...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Removed page heading per requirements */}

      {/* Status Summary Cards */}
      <Row className="mb-4 justify-content-center">
        <Col lg={2} md={4} sm={6} className="mb-3">
          <Widget className={s.summaryCard}>
            <div className="d-flex justify-content-between">
              <div>
                <div className="h4 mb-0">{statistics.total}</div>
                <div className="text-muted small">Total Devices</div>
              </div>
              <div className={`${s.cardIcon} ${s.total}`}>
                <i className="eva eva-cube-outline"></i>
              </div>
            </div>
          </Widget>
        </Col>
        <Col lg={2} md={4} sm={6} className="mb-3">
          <Widget className={s.summaryCard}>
            <div className="d-flex justify-content-between">
              <div>
                <div className="h4 mb-0 text-success">{statistics.online}</div>
                <div className="text-muted small">Online</div>
              </div>
              <div className={`${s.cardIcon} ${s.online}`}>
                <i className="eva eva-checkmark-circle-outline"></i>
              </div>
            </div>
          </Widget>
        </Col>
        <Col lg={2} md={4} sm={6} className="mb-3">
          <Widget className={s.summaryCard}>
            <div className="d-flex justify-content-between">
              <div>
                <div className="h4 mb-0 text-danger">{statistics.offline}</div>
                <div className="text-muted small">Offline</div>
              </div>
              <div className={`${s.cardIcon} ${s.offline}`}>
                <i className="eva eva-close-circle-outline"></i>
              </div>
            </div>
          </Widget>
        </Col>
      </Row>

      {/* Device Table */}
      <Row>
        <Col>
          <Widget>
            <MUIDataTable
              title=""
              data={devices}
              columns={columns}
              options={options}
            />
          </Widget>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Widget>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Maintenance History</h4>
            </div>
            <div className={s.maintenanceTabs}>
              <button
                type="button"
                className={classNames(s.maintenanceTabButton, {
                  [s.maintenanceTabButtonActive]:
                    maintenanceTab === "facility",
                })}
                onClick={() => handleMaintenanceTabChange("facility")}
              >
                Facility Maintenance
              </button>
              <button
                type="button"
                className={classNames(s.maintenanceTabButton, {
                  [s.maintenanceTabButtonActive]:
                    maintenanceTab === "building",
                })}
                onClick={() => handleMaintenanceTabChange("building")}
              >
                Building Maintenance
              </button>
            </div>
            <MUIDataTable
              title=""
              data={activeHistoryState.data}
              columns={maintenanceColumns}
              options={maintenanceOptions}
            />
          </Widget>
        </Col>
      </Row>

      {/* Add Device Modal */}
      <Modal isOpen={addDeviceModal} toggle={toggleAddDeviceModal} size="lg">
        <ModalHeader toggle={toggleAddDeviceModal}>
          <i className="eva eva-plus-outline mr-2"></i>
          Add New Device
        </ModalHeader>
        <Form onSubmit={handleAddDevice}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="thing_name">
                    Thing Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="thing_name"
                    id="thing_name"
                    placeholder="e.g., pyc001_bat1"
                    value={newDevice.thing_name}
                    onChange={handleInputChange}
                    required
                  />
                  <small className="text-muted">IoT device identifier used for monitoring</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="asset_type">Asset Type</Label>
                  <Input
                    type="select"
                    name="asset_type"
                    id="asset_type"
                    value={newDevice.asset_type}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Asset Type</option>
                    <option value="Battery">Battery</option>
                    <option value="Inverter">Inverter</option>
                    <option value="Meter">Meter</option>
                    <option value="Sensor">Sensor</option>
                    <option value="Controller">Controller</option>
                    <option value="Other">Other</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="type">Device Type</Label>
                  <Input
                    type="text"
                    name="type"
                    id="type"
                    placeholder="e.g., Battery System, Solar Inverter"
                    value={newDevice.type}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="location_postal">Location (Postal Code)</Label>
                  <Input
                    type="text"
                    name="location_postal"
                    id="location_postal"
                    placeholder="e.g., 12345"
                    value={newDevice.location_postal}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="facility_name">Facility Name</Label>
                  <Input
                    type="text"
                    name="facility_name"
                    id="facility_name"
                    placeholder="e.g., Main Building, Production Floor A"
                    value={newDevice.facility_name}
                    onChange={handleInputChange}
                  />
                  <small className="text-muted">Name or description of the facility</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                {/* Empty column for spacing */}
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="remote_url">Remote Access URL</Label>
                  <Input
                    type="url"
                    name="remote_url"
                    id="remote_url"
                    placeholder="https://device.example.com"
                    value={newDevice.remote_url}
                    onChange={handleInputChange}
                  />
                  <small className="text-muted">URL for remote device access</small>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="lat">Latitude</Label>
                  <Input
                    type="text"
                    name="lat"
                    id="lat"
                    placeholder="e.g., 1.290270"
                    value={newDevice.lat}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="lon">Longitude</Label>
                  <Input
                    type="text"
                    name="lon"
                    id="lon"
                    placeholder="e.g., 103.851959"
                    value={newDevice.lon}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="status">Initial Status</Label>
                  <Input
                    type="select"
                    name="status"
                    id="status"
                    value={newDevice.status}
                    onChange={handleInputChange}
                  >
                    <option value="offline">Offline</option>
                    <option value="online">Online</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="error">Error</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleAddDeviceModal} disabled={saving}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <i className="eva eva-plus-outline mr-1"></i>
                  Add Device
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Edit Device Modal */}
      <Modal isOpen={editDeviceModal} toggle={toggleEditDeviceModal} size="lg">
        <ModalHeader toggle={toggleEditDeviceModal}>
          <i className="eva eva-edit-outline mr-2"></i>
          Edit Device
        </ModalHeader>
        <Form onSubmit={handleUpdateDevice}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="edit_thing_name">
                    Thing Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="thing_name"
                    id="edit_thing_name"
                    placeholder="e.g., pyc001_bat1"
                    value={editDevice.thing_name}
                    onChange={handleEditInputChange}
                    required
                  />
                  <small className="text-muted">IoT device identifier used for monitoring</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="edit_asset_type">Asset Type</Label>
                  <Input
                    type="select"
                    name="asset_type"
                    id="edit_asset_type"
                    value={editDevice.asset_type}
                    onChange={handleEditInputChange}
                  >
                    <option value="">Select Asset Type</option>
                    <option value="Battery">Battery</option>
                    <option value="Inverter">Inverter</option>
                    <option value="Meter">Meter</option>
                    <option value="Sensor">Sensor</option>
                    <option value="Controller">Controller</option>
                    <option value="Other">Other</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="edit_type">Device Type</Label>
                  <Input
                    type="text"
                    name="type"
                    id="edit_type"
                    placeholder="e.g., Battery System, Solar Inverter"
                    value={editDevice.type}
                    onChange={handleEditInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="edit_location_postal">Location (Postal Code)</Label>
                  <Input
                    type="text"
                    name="location_postal"
                    id="edit_location_postal"
                    placeholder="e.g., 12345"
                    value={editDevice.location_postal}
                    onChange={handleEditInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="edit_facility_name">Facility Name</Label>
                  <Input
                    type="text"
                    name="facility_name"
                    id="edit_facility_name"
                    placeholder="e.g., Main Building, Production Floor A"
                    value={editDevice.facility_name}
                    onChange={handleEditInputChange}
                  />
                  <small className="text-muted">Name or description of the facility</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                {/* Empty column for spacing */}
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="edit_remote_url">Remote Access URL</Label>
                  <Input
                    type="url"
                    name="remote_url"
                    id="edit_remote_url"
                    placeholder="https://device.example.com"
                    value={editDevice.remote_url}
                    onChange={handleEditInputChange}
                  />
                  <small className="text-muted">URL for remote device access</small>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="edit_lat">Latitude</Label>
                  <Input
                    type="text"
                    name="lat"
                    id="edit_lat"
                    value={editDevice.lat}
                    onChange={handleEditInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="edit_lon">Longitude</Label>
                  <Input
                    type="text"
                    name="lon"
                    id="edit_lon"
                    value={editDevice.lon}
                    onChange={handleEditInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleEditDeviceModal} disabled={saving}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Updating...
                </>
              ) : (
                <>
                  <i className="eva eva-save-outline mr-1"></i>
                  Update Device
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceManagement;